import { RefObject, useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

// Owns its visibility state and scroll subscription so showing/hiding the button
// never re-renders the (potentially very expensive) message list.
export function ScrollToBottomButton({
  scrollContainerRef,
  threadId,
}: {
  scrollContainerRef: RefObject<HTMLDivElement>;
  /** Re-subscribes content observers when the thread (and its keyed content div) remounts. */
  threadId?: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let animationFrameId = 0;
    const update = () => {
      animationFrameId = 0;
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      setVisible((prev) => (prev ? distanceFromBottom > 150 : distanceFromBottom > 250));
    };
    const scheduleUpdate = () => {
      if (!animationFrameId) animationFrameId = requestAnimationFrame(update);
    };

    update();
    container.addEventListener('scroll', scheduleUpdate, { passive: true });
    // Content growth (streaming, expanding tables) changes the distance without a scroll event
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(container);
    for (const child of Array.from(container.children)) observer.observe(child);

    return () => {
      container.removeEventListener('scroll', scheduleUpdate);
      observer.disconnect();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [scrollContainerRef, threadId]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <Button
            variant={'white'}
            className="mb-4 size-9 rounded-full shadow-lg/4 ring-offset-transparent focus-visible:ring-offset-white"
            onClick={() =>
              scrollContainerRef.current?.scrollTo({
                top: scrollContainerRef.current.scrollHeight,
                behavior: 'smooth',
              })
            }
          >
            <Icon className="lucide--arrow-down" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
